import { Injectable } from '@angular/core';
import { ShellQuery } from '@shared/store/shell.query';
import { createInitialState, ShellStore } from '@shared/store/shell.store';
import { IShellContext, IShellContextBizCtx, IShellContextUI  } from '@shared/store/IShellContext';
import { map, Observable, Subject, take, tap } from 'rxjs';
import { RoleTypeEnum } from '@shared/enums';
import _ from 'lodash';
import { Order } from 'app/doctors/orders/orders.service';
import { IDisplayedRxForm } from '@shared/generalInterfaces';

@Injectable({ providedIn: 'root' })
export class ShellContextService {
	selectedLanguage$: Observable<string> = this.shellQuery.selectedLanguage$;
	
	contextBizContextChanged$ = new Subject<IShellContextBizCtx>();
	contextUiChanged$ = new Subject<IShellContextUI>();

	constructor(private shellQuery: ShellQuery,
				private shellStore: ShellStore) {
	}

	get selectedCompanyId$(): Observable<number> {
		return this.shellQuery.selectedCompanyId$;
	}

	get selectedContactId$(): Observable<number> {
		return this.shellQuery.selectedContactId$;
	}

	get roleType$(): Observable<RoleTypeEnum> {
		return this.shellQuery.roleType$;
	}

	get isAuth0$(): Observable<boolean> {
		return this.shellQuery.isAuth0$;
	}

	get isPing$(): Observable<boolean> {
		return this.shellQuery.isPing$;
	}

	getContext(): Observable<IShellContext> {
		return this.shellQuery.context$;
	}
	
	getOrder(): Observable<Order | IDisplayedRxForm> {
		return this.shellQuery.order$;
	}

	updateOrder(order: Order | IDisplayedRxForm | Partial<Order>): void {
		this.shellStore.update(state => {
			state.order = order as Order | IDisplayedRxForm;
		});
	}

	updateIsAuth0(isAuth0: boolean): void {
		this.shellStore.update(state => {
			state.isAuth0 = isAuth0;
		});
	}

	updateIsPing(isPing: boolean): void {
		this.shellStore.update(state => {
			state.isPing = isPing;
		});
	}

	getCompanyRegion(): Observable<string> {
		return this.shellQuery.context$.pipe(
			take(1),
			map((context: IShellContext) => context?.bizCtx?.region)
		);
	}

	updateContext(contextCallback: (context: IShellContext) => void): void;
	updateContext(partialContext: Partial<IShellContext>): void;
	updateContext(contextOrCallback: ((context: IShellContext) => void) | Partial<IShellContext> ): void {
		if (typeof contextOrCallback === 'function') {
			this.shellStore.update(state => {	
				let originalBizCtxSection = _.cloneDeep(state.context.bizCtx);
				let originalUiSection = _.cloneDeep(state.context.UI);		
				contextOrCallback(state.context);
				this.triggerContextBizCtxUpdatedIfNeed(originalBizCtxSection, _.cloneDeep(state.context.bizCtx));
				this.triggerContextUiUpdatedIfNeed(originalUiSection, _.cloneDeep(state.context.UI));
			});
		} else {
			this.shellStore.update(state => {
				let originalBizCtxSection = _.cloneDeep(state.context.bizCtx);
				let originalUiSection = _.cloneDeep(state.context.UI);	
				state.context = { ...state.context, ...contextOrCallback };
				this.triggerContextBizCtxUpdatedIfNeed(originalBizCtxSection, _.cloneDeep(state.context.bizCtx));
				this.triggerContextUiUpdatedIfNeed(originalUiSection, _.cloneDeep(state.context.UI));
			});
		}
	}

	isContextReady(): Observable<boolean> {
		return this.shellQuery.context$.pipe(
			map((context: IShellContext) => context?.bizCtx?.region?.length > 0
				&& context?.bizCtx?.companyId > 0 && context?.bizCtx?.contactId > 0 && context?.security?.accessToken?.length > 0)
		);
	}

	private triggerContextBizCtxUpdatedIfNeed(originalBizCtxSection: IShellContextBizCtx, newBizCtxSection: IShellContextBizCtx) {
		let bizCtxChanged = !_.isEqual(originalBizCtxSection, newBizCtxSection);
		if (bizCtxChanged)
			this.contextBizContextChanged$.next(newBizCtxSection);
	}

	private triggerContextUiUpdatedIfNeed(originalUiSection: IShellContextUI, newUiSection: IShellContextUI) {
		let uiSectionChanged = !_.isEqual(originalUiSection, newUiSection);
		if (uiSectionChanged)
			this.contextUiChanged$.next(newUiSection);
	}

	resetContext() {
		this.shellStore.update(state => {
			state.context = createInitialState().context;
			state.order = createInitialState().order;
			state.isAuth0 = createInitialState().isAuth0;
			state.isPing = createInitialState().isPing;
		});
	}

	resetContextWithoutSession() {
		this.getContext().pipe(take(1), tap(context => {
			let sessionSection = context.session;
			let securitySection = context.security;
			this.shellStore.update(state => {
				state.context = createInitialState().context;
				state.context.session = sessionSection;
				state.context.security = securitySection;
			});
		})).subscribe();
	}

	resetOrderData(resetPatientId: boolean = true) {
		let orderData ;
		if (resetPatientId) {
			orderData = { orderId: null, csmMessageId: null, patientId: null, rxId: null };
		} else {
			orderData = { orderId: null, csmMessageId: null, rxId: null };
		}

		this.shellStore.update(state => {
			state.context.bizCtx = { ...state.context.bizCtx, ...orderData };
			state.order = null;
		});
	}
}
